import { datadogLogs } from '@datadog/browser-logs'
import {
  OpenFeature,
  Client,
  EvaluationContext,
  JsonValue,
  ClientProviderEvents,
  FlagValueType,
} from '@openfeature/web-sdk'
import { getFallbackValues } from './inMemory'
import { makeProviders } from './providers'
import {
  FeatureFlagValues,
  FeatureFlagProviderConfig,
  FlagDefinitions,
  FeatureFlagProvider,
  FeatureFlagTargetingConfig,
} from './types'

/**
 *
 * @param flagValue
 */
function getFlagType<T>(flagValue: T): FlagValueType | undefined {
  return typeof flagValue as FlagValueType | undefined
}
/*
 *
 */

/**
 * A client for interacting with feature flags via OpenFeature.
 *
 * Supports multiple providers: Split, LaunchDarkly, and local in-memory flags.
 * When multiple remote providers are configured, the primary provider is used for
 * flag evaluation. Any additional configured providers are used for comparison only:
 * on each getFlagValues() call, their values are compared against the primary.
 *
 * Sample usage:
 *
 * ```typescript
 * const flagDefinitions = {
 *  exampleBooleanFlag: {
 *   local: true,
 *   fallback: false,
 *  },
 *  exampleStringFlag: {
 *   local: 'local',
 *   fallback: 'fallback',
 *  },
 * }
 *
 * const config = {
 *   primaryProvider: 'launchDarkly', // or 'local'
 *   launchDarklyClientSideId: 'your LaunchDarkly key', // optional
 * }
 *
 * const client = await ThymeFeatureFlagClient.createOpenFeatureClient(
 *   flagDefinitions,
 *   config,
 *   {
 *     targetingKey: 'traffic-key',
 *     trafficType: 'user',
 *     attributes: { customAttribute: 'value' }, // optional
 *   },
 * )
 *
 * const flags = client.getFlagValues()
 * console.log(flags.exampleBooleanFlag) // true
 *
 * // Listen for updates to the flags
 * client.onValuesUpdated(() => {
 *  console.log('Flags updated:', client.getFlagValues())
 * })
 *
 * // Clean up when done
 * await client.destroy()
 * ```
 */
export class ThymeFeatureFlagClient<T extends FlagDefinitions> {
  private constructor(
    private flagDefinitions: T,
    private clients: Partial<Record<FeatureFlagProvider, Client>>,
    private primaryProvider: FeatureFlagProvider
  ) {}

  static async createOpenFeatureClient<T extends FlagDefinitions>(
    flagDefinitions: T,
    providerConfig: FeatureFlagProviderConfig,
    targetingConfig: FeatureFlagTargetingConfig
  ): Promise<ThymeFeatureFlagClient<T>> {
    const context: EvaluationContext = {
      ...(targetingConfig.targetingKey
        ? { targetingKey: targetingConfig.targetingKey }
        : { anonymous: true }),
      kind: targetingConfig.trafficType,
      ...targetingConfig.attributes,
    }

    const clients: Partial<Record<FeatureFlagProvider, Client>> = {}
    for (const [name, provider] of Object.entries(
      makeProviders(providerConfig, flagDefinitions)
    )) {
      await OpenFeature.setProviderAndWait(name, provider)
      await OpenFeature.setContext(name, context)
      clients[name as FeatureFlagProvider] = OpenFeature.getClient(name)
    }

    return new ThymeFeatureFlagClient(
      flagDefinitions,
      clients,
      providerConfig.primaryProvider
    )
  }

  getKeys(): string[] {
    return Object.keys(this.flagDefinitions)
  }

  private evaluateVals(
    client: Client,
    name: FeatureFlagProvider
  ): FeatureFlagValues<T> {
    const values: Partial<FeatureFlagValues<T>> = {}

    for (const key of this.getKeys()) {
      let details, value
      const fallback = this.flagDefinitions[key].fallback
      try {
        switch (getFlagType(fallback)) {
          case 'boolean': {
            details = client.getBooleanDetails(key, fallback as boolean)
            value = details.value
            break
          }
          case 'string': {
            details = client.getStringDetails(key, fallback as string)
            value = details.value
            break
          }
          case 'number': {
            details = client.getNumberDetails(key, fallback as number)
            value = details.value
            break
          }
          default: {
            details = client.getObjectDetails(
              key,
              (fallback as JsonValue) ?? {}
            )
            value = details.value
            if (value && Object.keys(value as object).length === 0) {
              // To make it easier for consumers to check for presence of object flags,
              // we return undefined instead of empty object
              value = undefined
            }
            break
          }
        }
        if (details.errorCode) {
          if (name === this.primaryProvider) {
            throw new Error(
              `Error fetching flag details for key ${key}: ${details.errorCode}`
            )
          } else {
            console.warn(
              `Error fetching flag details for key ${key} from comparison provider (${name}): ${details.errorCode}`
            )
          }
        }
        // @ts-ignore
        values[key] = value
      } catch (err) {
        console.error(`Error evaluating flag ${key}:`, err)
        // @ts-ignore
        values[key] = fallback
      }
    }

    return values as FeatureFlagValues<T>
  }
  private primaryClient(): Client {
    if (Object.keys(this.clients).length === 0) {
      throw new Error('No feature flag providers are configured.')
    }
    return this.clients[this.primaryProvider]!
  }
  private comparisonClients(): Partial<Record<FeatureFlagProvider, Client>> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [this.primaryProvider]: _, ...comparisonClients } = this.clients
    return comparisonClients
  }

  /**
   * The OpenFeature MultiProvider ComparisonStrategy (1) raises an error
   * if any of the providers return an error and (2) doesn't work with how
   * Split handles object values.
   *
   * Instead, this compares the values from each provider manually from their
   * rendered values but doesn't throw an error if a provider fails -
   * we just log and skip comparison for that provider.
   * @param primaryValues
   */
  private evaluateComparisonClientValues(
    primaryValues: FeatureFlagValues<T>
  ): void {
    for (const [name, client] of Object.entries(this.comparisonClients())) {
      try {
        const comparisonValues = this.evaluateVals(
          client,
          name as FeatureFlagProvider
        )
        for (const key of this.getKeys()) {
          const primaryVal = primaryValues[key as keyof typeof primaryValues]
          const comparisonVal =
            comparisonValues[key as keyof typeof comparisonValues]
          if (JSON.stringify(primaryVal) !== JSON.stringify(comparisonVal)) {
            // Then in the mismatch handler:
            datadogLogs.logger.warn('Flag mismatch detected', {
              flagKey: key,
              primaryProvider: this.primaryProvider,
              comparisonProvider: name,
              primaryValue: JSON.stringify(primaryVal),
              comparisonValue: JSON.stringify(comparisonVal),
            })
          }
        }
      } catch (err) {
        datadogLogs.logger.error(
          `Error evaluating comparison provider ${name}: ${err}`
        )
      }
    }
  }

  getFlagValues(): FeatureFlagValues<T> {
    const primaryValues = this.evaluateVals(
      this.primaryClient(),
      this.primaryProvider
    )
    this.evaluateComparisonClientValues(primaryValues)

    return primaryValues
  }

  static getFallbackValues<T extends FlagDefinitions>(
    flagDefinitions: T
  ): FeatureFlagValues<T> {
    return getFallbackValues(flagDefinitions)
  }

  onValuesUpdated(callbackFn: () => void) {
    OpenFeature.addHandler(ClientProviderEvents.Ready, callbackFn)
    OpenFeature.addHandler(
      ClientProviderEvents.ConfigurationChanged,
      callbackFn
    )
  }

  async destroy() {
    await OpenFeature.close()
    OpenFeature.clearHandlers()
  }
}
